import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PostDetailModal from '../components/PostDetailModal';
import InterhellNavigation from '../components/InterhellNavigation';
import YouTubeEmbed from '../components/YouTubeEmbed';
import ConsciousCommerceGrid from '../components/ConsciousCommerceGrid';
import PrintifyProducts from '../components/PrintifyProducts';
import ProductFeedAd from '../components/ProductFeedAd';
import { FormattedContent } from '../utils/textUtils';
import { enhanceImageUrl } from '../utils/imageEnhancer';
import ConfiguredLowerSidebar from '../components/ConfiguredLowerSidebar';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper functions for the simplified layout
const getPostSizeClasses = (size) => {
  switch (size) {
    case 'large_full_width':
      return 'w-full bg-white rounded-xl shadow-lg overflow-hidden border transition-all duration-200 hover:shadow-xl cursor-pointer';
    case 'small_rectangle':
      return 'w-full bg-white rounded-lg shadow-md overflow-hidden border transition-all duration-200 hover:shadow-lg cursor-pointer min-h-[200px]';
    case 'large_banner':
      return 'w-full bg-white rounded-lg shadow-md overflow-hidden border transition-all duration-200 hover:shadow-lg cursor-pointer min-h-[240px]';
    case 'thin_banner':
      return 'w-full bg-white rounded-lg shadow-md overflow-hidden border transition-all duration-200 hover:shadow-lg cursor-pointer min-h-[80px]';
    default:
      return 'w-full bg-white rounded-lg shadow-sm overflow-hidden border transition-all duration-200 hover:shadow-md cursor-pointer min-h-[150px]';
  }
};

const renderPost = (post, onPostClick) => {
  const { type, size, content } = post;
  
  // Handle different post types
  switch (type) {
    case 'standard_post':
      return (
        <div className="p-4 h-full" onClick={() => onPostClick && onPostClick(content)}>
          {/* Post Header */}
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              {content.username ? content.username[0].toUpperCase() : 'M'}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900 text-sm">
                {content.username || 'Modern Human Being'}
              </div>
              <div className="text-xs text-gray-500">
                {new Date(content.created_at).toLocaleDateString()}
              </div>
            </div>
            {content.is_official && (
              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                ✨ Official
              </span>
            )}
          </div>

          {/* Post Title */}
          {content.title && (
            <h3 className="font-bold text-gray-900 text-lg leading-tight mb-3">
              {content.title}
            </h3>
          )}

          {/* YouTube Video */}
          {content.content_type === 'video' && content.video_url && (
            <div className="mb-3">
              <YouTubeEmbed 
                videoUrl={content.video_url}
                title={content.youtube_title || content.title}
              />
            </div>
          )}

          {/* Post Image - Display entire picture, no cropping */}
          {content.image && content.content_type !== 'video' && (
            <div className="mb-3">
              <img
                src={enhanceImageUrl(content.image)}
                alt={content.title || 'Post image'}
                className="w-full h-auto object-contain"
              />
            </div>
          )}

          {/* Post Content - Only 5 lines maximum */}
          {content.content && (
            <div className="text-gray-700 text-sm leading-relaxed">
              <div className="line-clamp-5">
                <FormattedContent content={content.content} />
              </div>
            </div>
          )}

          {/* Engagement Stats */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
            <div className="flex items-center space-x-3 text-xs text-gray-500">
              <span className="flex items-center space-x-1">
                <span>❤️</span>
                <span>{content.likes_count || 0}</span>
              </span>
              <span className="flex items-center space-x-1">
                <span>💬</span>
                <span>{content.comments_count || 0}</span>
              </span>
            </div>
          </div>
        </div>
      );
      
    case 'text_post':
      return (
        <div className="p-4 h-full">
          <div className="font-medium text-purple-800 text-sm mb-2">
            @{content.username || 'User'}
          </div>
          <div className="text-gray-700 text-sm">
            <FormattedContent content={content.content} />
          </div>
        </div>
      );
      
    case 'house_ad':
      return (
        <div className="p-4 h-full flex flex-col justify-center text-center">
          <div className="font-bold text-lg mb-2">🌿 {content.title || content.name}</div>
          <div className="text-base opacity-90 mb-2">{content.content || 'Conscious Commerce'}</div>
          {content.price && (
            <div className="font-bold text-xl">${content.price}</div>
          )}
        </div>
      );
      
    case 'affiliate_ad':
      return (
        <div className="p-4 h-full flex flex-col justify-center text-center">
          <div className="font-bold text-lg mb-2">🔗 {content.title}</div>
          <div className="text-base opacity-90 mb-2">{content.description || 'Partner Offer'}</div>
          <div className="bg-white bg-opacity-20 rounded px-3 py-2 inline-block text-sm">
            Learn More
          </div>
        </div>
      );
      
    case 'filler':
      return (
        <div className="p-4 h-full">
          <div className="font-medium text-blue-800 text-sm mb-2">
            💡 {content.title || 'Did You Know?'}
          </div>
          <div className="text-gray-700 text-sm">
            {content.content}
          </div>
        </div>
      );
      
    default:
      return (
        <div className="p-4 h-full flex items-center justify-center text-gray-500">
          Unknown Post Type: {type}
        </div>
      );
  }
};

const Dashboard = ({ isGuestMode = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [logoExpanded, setLogoExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [productAds, setProductAds] = useState([]);

  // Guest mode notification state
  const [showGuestNotification, setShowGuestNotification] = useState(false);

  useEffect(() => {
    // Reset page and posts when category changes
    setPosts([]);
    setPage(1);
    setHasMore(true);
    
    // Fetch random products for feed advertising
    fetchRandomProductsForAds();
    
    fetchPosts(1, true);
  }, [activeCategory, isGuestMode, token]);

  useEffect(() => {
    // Add scroll listener for infinite scroll
    const handleScroll = () => {
      // More forgiving scroll detection - trigger when within 100px of bottom
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 100) {
        loadMorePosts();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, page]);

  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;
    
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchPosts(nextPage, false);
  };

  useEffect(() => {
    if (isGuestMode && !showGuestNotification) {
      setShowGuestNotification(true);
      const timer = setTimeout(() => setShowGuestNotification(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isGuestMode, showGuestNotification]);

  const categories = [
    { id: 'all', label: 'All Posts', icon: '🌟' },
    { id: 'personal_observation', label: 'Personal Observation', icon: '👁️' },
    { id: 'escapes_adventure', label: 'Escapes and Adventure', icon: '🗻' },
    { id: 'spa_life', label: 'Spa Life', icon: '🧖' },
    { id: 'hot_springs', label: 'Hot Springs', icon: '♨️' },
    { id: 'consciousness', label: 'Consciousness', icon: '🧠' },
    { id: 'meditation', label: 'Meditation', icon: '🧘' },
    { id: 'health_activities', label: 'Health Activities', icon: '🏃' },
    { id: 'modalities', label: 'Modalities', icon: '⚡' },
    { id: 'survival_skills', label: 'Survival Skills', icon: '🏕️' },
    { id: 'emergency_preparedness', label: 'Emergency Preparedness', icon: '🚨' },
    { id: 'body_work', label: 'Body Work', icon: '💆' },
    { id: 'sexuality', label: 'Sexuality', icon: '🌹' },
    { id: 'vitamins_supplements', label: 'Vitamins and Supplements', icon: '💊' },
    { id: 'healthy_recipes', label: 'Healthy Recipes', icon: '🥗' },
    { id: 'longevity_practices', label: 'Longevity Practices', icon: '🌿' },
    { id: 'psychedelic_therapy', label: 'Psychedelic Therapy', icon: '🍄' },
    { id: 'music', label: 'Music', icon: '🎵' },
    { id: 'personal_coaching', label: 'Personal Coaching', icon: '👤' },
    { id: 'yoga', label: 'Yoga', icon: '🕉️' },
    { id: 'pranayama', label: 'Pranayama', icon: '🌬️' },
    { id: 'sustainable_homes', label: 'Sustainable Homes', icon: '🏡' },
    { id: 'energy_work', label: 'Energy Work', icon: '⚡' },
    { id: 'big_p_phenomena', label: 'Big P Phenomena', icon: '🔮' },
    { id: 'ufo_uap_strangeness', label: 'UFO/UAP High Strangeness', icon: '🛸' },
    { id: 'cause_market', label: 'Cause Market', icon: '📈' },
    { id: 'gadget_life', label: 'Gadget Life', icon: '📱' },
    { id: 'psionics', label: 'Psionics', icon: '🧠' },
    { id: 'cultiness', label: 'Cultiness', icon: '👥' },
    { id: 'intentional_communities', label: 'Intentional Communities', icon: '🏘️' }
  ];

  const fetchRandomProductsForAds = async () => {
    try {
      const response = await axios.get(`${API}/api/conscious-commerce/products?placement=filler&limit=10`);
      setProductAds(response.data || []);
    } catch (error) {
      console.error('Error fetching products for ads:', error);
    }
  };

  const fetchPosts = async (pageNum = 1, resetPosts = false) => {
    try {
      if (resetPosts) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const headers = {};
      if (!isGuestMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const params = {
        page: pageNum,
        limit: 12, // Increased limit for better initial load (6 standard posts + 6 mixed rows)
        ...(activeCategory !== 'all' ? { category: activeCategory } : {})
      };
      
      const response = await axios.get(`${API}/api/feed/dynamic-pattern`, { headers, params });
      const feedData = response.data;
      
      if (resetPosts) {
        setPosts(feedData.posts || []);
      } else {
        setPosts(prev => [...prev, ...(feedData.posts || [])]);
      }

      setHasMore(feedData.has_more || false);

    } catch (error) {
      console.error('Error fetching posts:', error);
      if (resetPosts) {
        setPosts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handlePostDeleted = (deletedPostId) => {
    setPosts(prevPosts => prevPosts.filter(post => post.id !== deletedPostId));
    setSelectedPost(null);
  };

  // Render mixed row items - mobile-friendly, max 2 rows between standard posts
  const renderMixedRowItem = (item) => {
    const { type, content, width, height } = item;

    switch (type) {
      case 'text_only_post':
        return (
          <div className={`bg-purple-50 border border-purple-200 rounded-lg p-3 h-full ${height === 2 ? 'flex flex-col' : ''}`}>
            <div className="font-medium text-purple-800 text-sm mb-2">
              @{content.username || 'Anonymous'}
            </div>
            <div className={`text-gray-700 text-sm ${height === 2 ? 'flex-1 overflow-hidden' : ''}`}>
              <FormattedContent content={content.content} />
            </div>
          </div>
        );

      case 'teaser_post':
        return (
          <div className={`bg-green-50 border border-green-200 rounded-lg p-3 h-full cursor-pointer hover:bg-green-100 transition-colors ${height === 2 ? 'flex flex-col' : ''}`}>
            <div className="font-medium text-green-800 text-sm mb-1">
              {content.title}
            </div>
            <div className={`text-gray-600 text-xs ${height === 2 ? 'flex-1' : ''}`}>
              {content.content}
            </div>
            <div className="text-green-600 text-xs mt-2">
              Click to view original →
            </div>
          </div>
        );

      case 'house_ad':
        return (
          <div className={`bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-lg p-3 h-full flex flex-col justify-center text-center`}>
            <div className="font-bold text-sm mb-1">🌿 CONSCIOUS COMMERCE</div>
            <div className={`opacity-90 ${height === 2 ? 'text-sm mb-2' : 'text-sm mb-1'}`}>{content.title || content.name}</div>
            {content.price && (
              <div className={`font-bold ${height === 2 ? 'text-xl' : 'text-lg'}`}>${content.price}</div>
            )}
          </div>
        );

      case 'affiliate_ad':
        // Handle both text (1x1) and banner (2x1) affiliate ads
        const isTextAd = item.format === "text" || width === 1;
        return (
          <div className={`${isTextAd ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-gradient-to-br from-purple-600 to-indigo-700'} text-white rounded-lg p-3 h-full flex flex-col justify-center text-center cursor-pointer hover:scale-105 transition-transform`}>
            <div className={`font-bold ${isTextAd ? 'text-xs' : 'text-sm'} mb-1`}>
              {isTextAd ? '🔗' : '🔗'} {content.title}
            </div>
            {content.description && !isTextAd && (
              <div className="text-sm opacity-90 mb-2">{content.description}</div>
            )}
            <div className={`bg-white bg-opacity-20 rounded px-2 py-1 inline-block ${isTextAd ? 'text-xs' : 'text-xs'}`}>
              {isTextAd ? 'View' : 'Learn More'}
            </div>
          </div>
        );

      case 'filler':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 h-full">
            <div className="font-medium text-blue-800 text-sm mb-1">
              💡 {content.title || 'Did You Know?'}
            </div>
            <div className="text-gray-700 text-xs">
              {content.content}
            </div>
          </div>
        );

      case 'mini_aff_quad':
        // 4 mini-affs in 2x1 space (perfect for mobile flow)
        return (
          <div className="grid grid-cols-2 grid-rows-2 gap-1 h-full">
            {content.mini_affs.map((affText, idx) => (
              <div key={idx} className="bg-gradient-to-br from-yellow-200 to-orange-300 rounded-lg p-1 flex items-center justify-center">
                <div className="text-gray-800 font-medium text-center italic text-xs">
                  {affText}
                </div>
              </div>
            ))}
          </div>
        );

      case 'mini_aff':
        return (
          <div className="bg-gradient-to-br from-yellow-200 to-orange-300 rounded-lg p-2 flex items-center justify-center h-full" style={{minHeight: '50px'}}>
            <div className="text-gray-800 font-medium text-center italic text-xs">
              {content.content}
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 h-full flex items-center justify-center text-gray-500 text-sm">
            Unknown: {type}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading your feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      {/* Guest Mode Notification */}
      {showGuestNotification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3">
            <span className="text-lg">👋</span>
            <div>
              <div className="font-semibold">Welcome, Guest!</div>
              <div className="text-sm">You're browsing in view-only mode. Sign up to interact!</div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-10 lg:grid-cols-12 gap-6">
          
          {/* Left Sidebar - Categories & MHB Logo */}
          <div className="order-3 md:order-1 md:col-span-2 lg:col-span-2">
            <div className="space-y-6">
              
              {/* MHB Logo */}
              <div className="bg-white rounded-xl shadow-lg cursor-pointer" onClick={() => setLogoExpanded(true)}>
                <img 
                  src="https://i0.wp.com/modernhumanbeing.com/wp-content/uploads/2025/07/mhb-chat-logo.png?w=1024&ssl=1" 
                  alt="Modern Human Being" 
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              
              {/* Categories */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
                  <h3 className="text-lg font-semibold text-emerald-800">Areas of Study</h3>
                </div>
                <div className="p-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors text-sm font-medium ${
                        activeCategory === category.id
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      <span className="mr-2">{category.icon}</span>
                      {category.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Feed - DYNAMIC MASONRY LAYOUT */}
          <div className="order-1 md:order-2 md:col-span-6 lg:col-span-7">
            {/* Leaderboard Banner - matches the main feed column width & flexibility */}
            <div className="mb-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-4 text-center cursor-pointer hover:scale-105 transition-transform shadow-lg">
              <div className="font-bold text-lg">🌟 PREMIUM LEADERBOARD AD</div>
              <div className="text-sm opacity-90">Transform Your Life - 7-Day Mindfulness Retreat</div>
            </div>

            {posts.length === 0 && !loading ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="text-6xl mb-4">🌱</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">No posts yet!</h2>
                <p className="text-gray-600 mb-4">Be the first to share something inspiring!</p>
                <button 
                  onClick={() => navigate('/create-post')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-shadow"
                >
                  Create First Post
                </button>
              </div>
            ) : (
              <>
                {/* Main Feed - Compact Layout with Side-by-Side Rectangles */}
                <div className="space-y-3">
                  {posts.reduce((acc, post, index) => {
                    if (post === null) return acc; // Skip already processed posts
                    
                    if (post.size === 'large_full_width') {
                      // Standard posts - full width
                      acc.push(
                        <div key={`post-${index}`} className={getPostSizeClasses(post.size)}>
                          {renderPost(post, (postContent) => setSelectedPost(postContent))}
                        </div>
                      );
                    } else if (post.size === 'large_banner' || post.size === 'thin_banner') {
                      // Banner posts - full width
                      acc.push(
                        <div key={`post-${index}`} className={getPostSizeClasses(post.size)}>
                          {renderPost(post, (postContent) => setSelectedPost(postContent))}
                        </div>
                      );
                    } else if (post.size === 'small_rectangle') {
                      // Find next rectangle to pair with
                      let pairIndex = -1;
                      for (let i = index + 1; i < posts.length; i++) {
                        if (posts[i] && posts[i].size === 'small_rectangle') {
                          pairIndex = i;
                          break;
                        }
                      }
                      
                      if (pairIndex !== -1) {
                        // Found pair - render both together
                        const nextPost = posts[pairIndex];
                        acc.push(
                          <div key={`pair-${index}-${pairIndex}`} className="grid grid-cols-2 gap-4">
                            <div className="bg-white rounded-lg shadow-md overflow-hidden border transition-all duration-200 hover:shadow-lg cursor-pointer">
                              {renderPost(post, (postContent) => setSelectedPost(postContent))}
                            </div>
                            <div className="bg-white rounded-lg shadow-md overflow-hidden border transition-all duration-200 hover:shadow-lg cursor-pointer">
                              {renderPost(nextPost, (postContent) => setSelectedPost(postContent))}
                            </div>
                          </div>
                        );
                        // Mark the paired post as processed
                        posts[pairIndex] = null;
                      }
                      // If no pair found, skip single rectangles (no sets of 1)
                    }
                    return acc;
                  }, [])}
                </div>
              </>
            )}
              
            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <p className="ml-3 text-emerald-700 font-medium">Loading more posts...</p>
              </div>
            )}
            
            {/* End of Feed Indicator */}
            {!hasMore && posts.length > 0 && (
              <div className="flex justify-center items-center py-8">
                <p className="text-gray-500 font-medium">🌟 You've reached the end of your feed! 🌟</p>
              </div>
            )}
          </div>

          {/* Right Sidebar - Conscious Shopping */}
          <div className="order-2 md:order-3 md:col-span-2 lg:col-span-3">
            <div className="space-y-6">
              {/* Top: 2 Printify Products */}
              <PrintifyProducts layout="sidebar" maxProducts={2} />

              {/* InterhellNavigation */}
              <InterhellNavigation />

              {/* Long Sidebar: Uses Saved Configuration */}
              <ConfiguredLowerSidebar />
            </div>
          </div>
        </div>
      </div>

      {/* Post Detail Modal */}
      {selectedPost && (
        <PostDetailModal 
          post={selectedPost} 
          categories={categories.map(c => ({ key: c.id, label: c.label }))}
          onClose={() => setSelectedPost(null)}
          onPostDeleted={handlePostDeleted}
        />
      )}

      {/* Logo Expanded Modal */}
      {logoExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
          onClick={() => setLogoExpanded(false)}
        >
          <img 
            src="https://i0.wp.com/modernhumanbeing.com/wp-content/uploads/2025/07/mhb-chat-logo.png?w=1024&ssl=1" 
            alt="Modern Human Being" 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;